import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import Loading from '../components/Loading';
import Attachments from '../components/Attachments';

const EmployeeWiseSheet = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // employee object
  const [sheet, setSheet] = useState(null); // { employee, totals, count, expenses }
  const [sheetLoading, setSheetLoading] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', employeeId: '', email: '', password: '', department: '' });
  const [saving, setSaving] = useState(false);
  const [viewExpense, setViewExpense] = useState(null);

  const toast = useToast();

  const loadEmployees = async () => {
    try {
      const res = await axios.get('/api/admin/employees');
      setEmployees(res.data);
    } catch (e) {
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEmployees(); }, []);

  const loadSheet = async (emp) => {
    setSelected(emp);
    setSheetLoading(true);
    try {
      const res = await axios.get(`/api/employee-expenses/employee/${emp._id}`);
      setSheet(res.data);
    } catch (e) {
      toast.error('Failed to load employee sheet');
    } finally {
      setSheetLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!addForm.name || !addForm.employeeId || !addForm.password) {
      return toast.error('Name, Employee ID and Password are required');
    }
    setSaving(true);
    try {
      await axios.post('/api/admin/employees', addForm);
      toast.success('Employee created');
      setAddOpen(false);
      setAddForm({ name: '', employeeId: '', email: '', password: '', department: '' });
      loadEmployees();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create employee');
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(v) || 0);

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(`${d.slice(0, 10)}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const statusBadge = (s) => {
    const map = {
      pending: { background: '#fef3c7', color: '#92400e' },
      approved: { background: '#d1fae5', color: '#065f46' },
      rejected: { background: '#fee2e2', color: '#991b1b' },
    };
    const st = map[s] || map.pending;
    return <span className="badge" style={st}>{s}</span>;
  };

  if (loading) return <Loading />;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Employee-wise Expense Sheets</h1>
          <p className="page-subtitle">Open an individual employee's expense history</p>
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setAddOpen(true)}>+ Add Employee</button>
        )}
      </div>

      <div className="card">
        <div className="card-body">
          {employees.length === 0 ? (
            <div className="empty-state">
              <h3 className="empty-state-title">No employees yet</h3>
              <p className="empty-state-text">Add an employee to get started</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Department</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map((em) => (
                    <tr key={em._id}>
                      <td style={{ fontWeight: 600 }}>{em.employeeId || '—'}</td>
                      <td>{em.name}</td>
                      <td>{em.email}</td>
                      <td>{em.department}</td>
                      <td style={{ textAlign: 'center' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => loadSheet(em)}>Open Sheet</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selected && (
        <div style={{ marginTop: '24px' }}>
          <div className="page-header" style={{ marginBottom: '12px' }}>
            <div>
              <h2 className="page-title" style={{ fontSize: '1.25rem' }}>
                {selected.name} <span style={{ color: 'var(--gray-500)', fontWeight: 500 }}>({selected.employeeId || 'no ID'})</span>
              </h2>
            </div>
            <button className="btn btn-outline btn-sm" onClick={() => { setSelected(null); setSheet(null); }}>Close</button>
          </div>

          {sheetLoading ? (
            <Loading />
          ) : sheet ? (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon blue">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Expenses</div>
                    <div className="stat-value">{formatCurrency(sheet.totals.totalAmount)}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon green">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Paid</div>
                    <div className="stat-value">{formatCurrency(sheet.totals.totalPaid)}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon yellow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Balance</div>
                    <div className="stat-value">{formatCurrency(sheet.totals.totalBalance)}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon red">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Entries</div>
                    <div className="stat-value">{sheet.count}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon blue">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Employee Payable</div>
                    <div className="stat-value">{formatCurrency(sheet.totals.totalEmployeePayable)}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon green">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Total Paid by Admin</div>
                    <div className="stat-value">{formatCurrency(sheet.totals.totalPaidByAdmin ?? sheet.totals.totalAdminPaid)}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon yellow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Advance Paid</div>
                    <div className="stat-value">{formatCurrency(sheet.totals.totalAdvancePaid)}</div>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon blue">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="5" width="20" height="14" rx="2" /><line x1="2" y1="10" x2="22" y2="10" /></svg>
                  </div>
                  <div className="stat-content">
                    <div className="stat-label">Remaining Payable Balance</div>
                    <div className="stat-value">{formatCurrency(sheet.totals.remainingPayableBalance ?? sheet.totals.totalPayableBalance)}</div>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginTop: '16px' }}>
                <div className="card-body">
                  {sheet.expenses.length === 0 ? (
                    <div className="empty-state"><h3 className="empty-state-title">No expenses</h3></div>
                  ) : (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Expense ID</th>
                            <th>Date</th>
                            <th>Nature</th>
                            <th>Contractor</th>
                            <th>Bill No.</th>
                            <th style={{ textAlign: 'right' }}>Amount</th>
                            <th style={{ textAlign: 'right' }}>Paid</th>
                            <th style={{ textAlign: 'right' }}>Balance</th>
                            <th style={{ textAlign: 'right' }}>Admin Paid</th>
                            <th style={{ textAlign: 'right' }}>Payable</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'center' }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sheet.expenses.map((e) => (
                            <tr key={e._id}>
                              <td style={{ fontWeight: 600 }}>{e.expenseId}</td>
                              <td>{formatDate(e.date)}</td>
                              <td>{e.expenseNature}</td>
                              <td>{e.contractorName}</td>
                              <td>{e.billNumber || '—'}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(e.amount)}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(e.paidAmount)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: Number(e.balanceAmount) > 0 ? 'var(--danger)' : 'var(--success)' }}>{formatCurrency(e.balanceAmount)}</td>
                              <td style={{ textAlign: 'right' }}>{formatCurrency(e.adminPaidAmount)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--primary)' }}>
                                {Number(e.payableBalance) === 0 && Number(e.employeePayable) > 0
                                  ? <span className="badge" style={{ background: '#d1fae5', color: '#065f46' }}>Settled</span>
                                  : formatCurrency(e.payableBalance)}
                              </td>
                              <td>{statusBadge(e.status)}</td>
                              <td style={{ textAlign: 'center' }}>
                                <button className="btn btn-outline btn-sm" onClick={() => setViewExpense(e)}>View</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Add employee modal */}
      {addOpen && (
        <div className="modal-overlay" onClick={() => setAddOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '460px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Add Employee</h3>
              <button className="modal-close" onClick={() => setAddOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-input" value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Employee ID *</label>
                <input className="form-input" value={addForm.employeeId} onChange={(e) => setAddForm({ ...addForm, employeeId: e.target.value })} placeholder="e.g. EMP001" />
              </div>
              <div className="form-group">
                <label className="form-label">Email (optional)</label>
                <input type="email" className="form-input" value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Password *</label>
                <input type="text" className="form-input" value={addForm.password} onChange={(e) => setAddForm({ ...addForm, password: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Department</label>
                <input className="form-input" value={addForm.department} onChange={(e) => setAddForm({ ...addForm, department: e.target.value })} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setAddOpen(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* View expense modal */}
      {viewExpense && (
        <div className="modal-overlay" onClick={() => setViewExpense(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '560px' }}>
            <div className="modal-header">
              <h3 className="modal-title">Expense {viewExpense.expenseId}</h3>
              <button className="modal-close" onClick={() => setViewExpense(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '0.9rem' }}>
                <div><strong>Date:</strong> {formatDate(viewExpense.date)}</div>
                <div><strong>Status:</strong> {statusBadge(viewExpense.status)}</div>
                <div><strong>Nature:</strong> {viewExpense.expenseNature}</div>
                <div><strong>Contractor:</strong> {viewExpense.contractorName}</div>
                <div><strong>Project:</strong> {viewExpense.projectName || '—'}</div>
                <div><strong>Bill No:</strong> {viewExpense.billNumber || '—'}</div>
                <div><strong>Payment Mode:</strong> {viewExpense.paymentMode}</div>
                <div><strong>Paid By:</strong> {viewExpense.paidBy || '—'}</div>
                <div><strong>Amount:</strong> {formatCurrency(viewExpense.amount)}</div>
                <div><strong>Paid:</strong> {formatCurrency(viewExpense.paidAmount)}</div>
                <div><strong>Balance:</strong> {formatCurrency(viewExpense.balanceAmount)}</div>
              </div>
              {viewExpense.remarks && <div className="form-group" style={{ marginTop: '14px' }}><label className="form-label">Remarks</label><div style={{ fontSize: '0.9rem' }}>{viewExpense.remarks}</div></div>}
              {viewExpense.adminRemark && <div className="form-group" style={{ marginTop: '14px' }}><label className="form-label">Admin Remark</label><div className="alert" style={{ fontSize: '0.9rem' }}>{viewExpense.adminRemark}</div></div>}
              <div className="form-group" style={{ marginTop: '14px' }}>
                <label className="form-label">Attachments</label>
                <Attachments attachments={viewExpense.attachments} />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setViewExpense(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeWiseSheet;